import React, { useRef, useState, type ReactNode } from "react";
import { cn } from "../lib/cn";
import { controlBase, controlOpen, controlSizes, type ControlSize } from "../lib/control";
import { useFieldControl } from "./field";
import { useIsMobile } from "../lib/use-is-mobile";
import { BottomSheet } from "./bottom-sheet";
import { Popover } from "./popover";

export interface SelectProps extends Omit<React.ComponentPropsWithRef<"button">, "size" | "onChange" | "value" | "defaultValue"> {
  size?: ControlSize;
  /** Option non sélectionnable affichée en tête, façon placeholder. */
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  name?: string;
}

/**
 * Sélecteur "Select" 100% custom qui remplace le `<select>` natif.
 * Sur mobile, il ouvre un BottomSheet natif SOTA (Vaul).
 * Sur desktop, il affiche un popover classique.
 */
export function Select({
  className,
  size = "md",
  placeholder = "Sélectionner...",
  children,
  defaultValue,
  value,
  onChange,
  disabled,
  id,
  name,
  ...props
}: SelectProps) {
  const fieldProps = useFieldControl(props);
  
  // Extraire les options depuis les enfants (qui sont typiquement des <option>)
  const options: { value: string; label: ReactNode; disabled?: boolean }[] = [];
  
  React.Children.forEach(children, (child) => {
    if (React.isValidElement(child)) {
      if (child.type === "option") {
        const props = child.props as any;
        options.push({
          value: props.value,
          label: props.children,
          disabled: props.disabled,
        });
      } else if (child.type === React.Fragment) {
        const props = child.props as any;
        React.Children.forEach(props.children, (subChild) => {
          if (React.isValidElement(subChild) && subChild.type === "option") {
            const subProps = subChild.props as any;
            options.push({
              value: subProps.value,
              label: subProps.children,
              disabled: subProps.disabled,
            });
          }
        });
      }
    }
  });

  const [internal, setInternal] = useState<string>(defaultValue ?? "");
  const isControlled = value !== undefined;
  const currentValue = isControlled ? value : internal;

  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const selectedOption = options.find((o) => o.value === currentValue);

  const selectOption = (val: string) => {
    if (!isControlled) setInternal(val);
    onChange?.(val);
    setIsOpen(false);
  };

  const listContent = (
    <div className="flex flex-col py-1">
      {options.length === 0 && (
        <div className="px-4 py-3 text-center text-sm text-fg-muted">
          Aucune option
        </div>
      )}
      {options.map((opt, i) => {
        const isSelected = opt.value === currentValue;
        return (
          <button
            key={i}
            type="button"
            disabled={opt.disabled}
            onClick={() => selectOption(opt.value)}
            className={cn(
              // `tint` : le survol est un calque déjà peint dont seule
              // l'opacité varie. Sur une liste qu'on parcourt au curseur, on
              // traverse des dizaines d'options par seconde — autant d'entrées
              // et de sorties qui, en `background-color`, sont autant de
              // repaints. Le contenu de l'option reste au-dessus du calque.
              "tint flex w-full items-center justify-between px-4 py-3 sm:py-2.5 text-left text-sm touch-manipulation",
              "focus:bg-surface-sunken focus:outline-none",
              isSelected ? "font-semibold text-accent" : "text-fg",
              opt.disabled && "opacity-50 cursor-not-allowed pointer-events-none"
            )}
          >
            <span className="truncate">{opt.label}</span>
            {isSelected && (
              <svg className="size-4 shrink-0 text-accent" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 10l4 4 8-8" />
              </svg>
            )}
          </button>
        );
      })}
    </div>
  );

  return (
    <div ref={containerRef} className="relative inline-block w-full">
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className={cn(
          controlBase,
          controlSizes[size],
          "flex w-full cursor-pointer items-center justify-between gap-2 text-left touch-manipulation",
          isOpen && controlOpen,
          className
        )}
        {...fieldProps}
      >
        <span className={cn("truncate", !selectedOption && "text-fg-muted")}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <svg
          className={cn(
            "size-4 shrink-0 text-fg-subtle transform-gpu transition-transform duration-normal ease-standard",
            isOpen && "rotate-180 text-accent"
          )}
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="m6 8 4 4 4-4" />
        </svg>
      </button>

      {/* Input caché pour supporter la soumission de formulaire natif si on utilise name */}
      {name && <input type="hidden" name={name} value={currentValue} disabled={disabled} />}

      {isMobile ? (
        <BottomSheet
          open={isOpen}
          onClose={() => setIsOpen(false)}
          title={placeholder !== "Sélectionner..." ? placeholder : undefined}
        >
          <div className="pb-safe">
            {listContent}
          </div>
        </BottomSheet>
      ) : (
        // La liste est portalisée dans la top layer : elle passe au-dessus des
        // cartes voisines et n'est plus découpée par un ancêtre défilant.
        <Popover
          anchorRef={containerRef}
          open={isOpen}
          onClose={() => setIsOpen(false)}
          matchAnchorWidth
          className="max-h-60"
        >
          {listContent}
        </Popover>
      )}
    </div>
  );
}
